import React from 'react';
import {
    DAuditInfo,
    IAuditStamps,
    RhCheckbox,
    RhFormWrapper,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    ActivityEntityName,
    clearMaintenanceModule,
    useUpsertMachineMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import { BranchInput } from './branch/branch-input';
import { OrderProductionTypeInput } from './order-production-type/order-production-type-input';
import { FIELD_LABELS, shortLabel } from '../../../../../../helpers';

export interface IMachine {
    name: string;
    id?: number | null;
    branch_id?: number | null;
    order_production_type_id?: number | null;
    discontinued: boolean;
    consumes_input: boolean;
}

interface IMachineForm {
    // Audit stamps ride alongside the form values — display only, never
    // submitted, and deliberately kept out of IMachine (see IAuditStamps).
    machine?: (IMachine & IAuditStamps) | null;
    onSuccess: () => void;
    disabled: boolean;
}

export const MachineForm = (props: IMachineForm) => {
    const { machine, onSuccess, disabled } = props;

    const [mutation, { loading }] = useUpsertMachineMutation({
        update: (cache) => {
            clearMaintenanceModule(cache);
        },
    });

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.Machine,
        entityId: machine?.id,
        unsavedMessage:
            'El historial aparece una vez que la máquina ha sido guardada.',
        emptyMessage: 'No hay actividades registradas para esta máquina.',
    });

    const initialValues: IMachine = {
        name: machine?.name || '',
        order_production_type_id: machine?.order_production_type_id || null,
        branch_id: machine ? machine.branch_id : null,
        discontinued: machine?.discontinued || false,
        // Defaults to true for a new machine, matching the column default: most
        // machines consume a product as input. The packing áreas are the
        // exception, not the rule.
        consumes_input: machine ? machine.consumes_input : true,
    };

    const validationSchema: SchemaOf<IMachine> = yup.object({
        id: yup.number().nullable().notRequired(),
        name: yup.string().required(),
        discontinued: yup.boolean().required(),
        consumes_input: yup.boolean().required(),
        branch_id: yup
            .number()
            .required(`'Sucursal' es obligatorio`)
            .nullable(),
        order_production_type_id: yup
            .number()
            .required(`'Orden de producción' es obligatorio`)
            .nullable(),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                machine?.id ? (
                    <DAuditInfo
                        created_by={machine.created_by}
                        updated_by={machine.updated_by}
                    />
                ) : null
            }
            onSubmit={async (data, event) => {
                await mutation({
                    variables: {
                        MachineUpsertInput: {
                            id: machine?.id || null,
                            name: data.name,
                            branch_id: data.branch_id,
                            order_production_type_id:
                                data.order_production_type_id,
                            discontinued: data.discontinued,
                            consumes_input: data.consumes_input,
                        },
                    },
                });
                onSuccess();
                return;
            }}
            onInvalid={async (errors) => {
                return;
            }}
        >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: [
                            'name',
                            'branch_id',
                            'order_production_type_id',
                        ],
                        content: (
                            <>
                                <RHTextField
                                    name={'name'}
                                    label={shortLabel(FIELD_LABELS.name)}
                                />
                                <BranchInput />
                                <OrderProductionTypeInput />
                            </>
                        ),
                    },
                    {
                        label: 'Configuración',
                        optional: true,
                        fields: ['discontinued', 'consumes_input'],
                        content: (
                            <>
                                <RhCheckbox
                                    name={'discontinued'}
                                    label={shortLabel(
                                        FIELD_LABELS.discontinued,
                                    )}
                                />
                                <RhCheckbox
                                    name={'consumes_input'}
                                    label={shortLabel(
                                        FIELD_LABELS.consumes_input,
                                    )}
                                />
                            </>
                        ),
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
};
